import * as THREE from 'three'

import RenderItem from 'Renderer/RenderItem'

class GhostItem extends RenderItem {
    constructor(id, renderer, parent, onUpdate, planeId) {
        super(id, renderer)

        this.onUpdate = onUpdate

        this.parent = parent
        this.furnitureData = null
        this.planeId = planeId

        this.visible = false
        this.geometry = null
        this.renderObject = null

        this.material = null
        this.bbox = new THREE.Box3()
    }

    setFurnitureData(furnitureData) {
        if (this.furnitureData && 
            furnitureData &&
            this.furnitureData.id === furnitureData.id) {
            return
        }

        this.furnitureData = furnitureData
        this.recreateRenderObject()
    }

    setVisible(visible) {
        if (visible === this.visible || !this.renderObject) {
            return
        }

        this.visible = visible
        this.renderObject.visible = visible
        this.Renderer.onChildUpdate()

        this.onUpdate({
            corners: this.projectBBox(),
        })
    }

    projectBBox() {
        const boxCorners = [
            new THREE.Vector3(this.bbox.min.x, this.bbox.max.y, this.bbox.max.z),
            new THREE.Vector3(this.bbox.min.x, this.bbox.min.y, this.bbox.max.z),
            new THREE.Vector3(this.bbox.min.x, this.bbox.min.y, this.bbox.min.z),
            new THREE.Vector3(this.bbox.min.x, this.bbox.max.y, this.bbox.min.z),
            new THREE.Vector3(this.bbox.max.x, this.bbox.max.y, this.bbox.max.z),
            new THREE.Vector3(this.bbox.max.x, this.bbox.min.y, this.bbox.max.z),
            new THREE.Vector3(this.bbox.max.x, this.bbox.min.y, this.bbox.min.z),
            new THREE.Vector3(this.bbox.max.x, this.bbox.max.y, this.bbox.min.z),
        ]
        .map(position => position.project(this.Renderer.camera))
        .map(position => 
            new THREE.Vector2(position.x * 0.5 + 0.5, position.y * 0.5 + 0.5))

        let minX = 9999
        let maxX = -9999
        let minY = 9999
        let maxY = -9999
        for (let i = 0; i < boxCorners.length; i += 1) {
            minX = Math.min(minX, boxCorners[i].x)
            maxX = Math.max(maxX, boxCorners[i].x)
            minY = Math.min(minY, boxCorners[i].y)
            maxY = Math.max(maxY, boxCorners[i].y)
        }
        return [minX, maxX, minY, maxY]
    }

    recreateRenderObject() {
        this.clearRenderObject()

        let sx = 0.5 / this.furnitureData.scale
        let sy = 0.5 / this.furnitureData.scale
        let sz = 0.5 / this.furnitureData.scale
        if (this.furnitureData.min && this.furnitureData.max) {
            sx = this.furnitureData.max.x - this.furnitureData.min.x
            sy = this.furnitureData.max.y - this.furnitureData.min.y
            sz = this.furnitureData.max.z - this.furnitureData.min.z
        }
        this.geometry = new THREE.BoxGeometry(sx, sy, sz)
        if (this.furnitureData.min) {
            this.geometry.translate(
                this.furnitureData.min.x + sx * 0.5,
                this.furnitureData.min.y + sy * 0.5,
                this.furnitureData.min.z + sz * 0.5,
            )
        } else {
            this.geometry.translate(0, 0.5 * sy, 0)
        }

        this.material = new THREE.MeshPhongMaterial({
            color: 0xFFFFFF,
            emissive: 0x888888,
        })
        this.renderObject = new THREE.Mesh(this.geometry, this.material)

        this.renderObject.matrixAutoUpdate = false
        this.renderObject.matrix.scale(new THREE.Vector3(this.furnitureData.scale, 
            this.furnitureData.scale, this.furnitureData.scale))
        this.renderObject.matrix.multiply(new THREE.Matrix4().makeRotationX(this.furnitureData.rotX))

        if (this.parent) {
            this.parent.addChild(this)
        }
        this.renderObject.updateWorldMatrix()

        this.bbox.makeEmpty()
        this.bbox.expandByObject(this.renderObject)
    }

    clearRenderObject() {
        if (this.renderObject && this.parent) {
            this.parent.removeChild(this)
        }

        this.renderObject = null

        if (this.geometry) {
            this.geometry.dispose()
        }
        this.geometry = null
    }

    setPosition(xy) {
        if (this.planeId === null) {
            return
        }

        const surface = this.Renderer.getStackingSurface(this.planeId)
        if (!surface) {
            return
        }

        const renderDimensions = this.Renderer.getRenderDimensions()
        this.Renderer.setupRaycaster(xy[0] / renderDimensions[0], xy[1] / renderDimensions[1])
        const intersections = this.Renderer.raycaster.intersectObject(surface.renderObject)

        if (intersections && intersections.length > 0) {
            let p = intersections[0].point
            if (this.parent) {
                p = this.parent.getObject().worldToLocal(p)

                this.renderObject.matrix.makeTranslation(p.x, p.y, p.z)
                this.renderObject.matrix.scale(new THREE.Vector3(this.furnitureData.scale, 
                    this.furnitureData.scale, this.furnitureData.scale))
                this.renderObject.matrix.multiply(new THREE.Matrix4().makeRotationX(this.furnitureData.rotX))
                this.getObject().updateWorldMatrix()

                this.bbox.makeEmpty()
                this.bbox.expandByObject(this.renderObject)
                this.onUpdate({
                    corners: this.projectBBox(),
                })
                this.Renderer.onChildUpdate()
            }
        }
    }
}

export default GhostItem